import type { Country } from '@tenders/shared';
import { countryConfig } from '../country';

/**
 * Russian prepositional-case ("предложный падеж") region names.
 *
 * The RU region strings read "Тендеры и госзакупки в {{region}}", but the API
 * hands back the nominative name, so every region page shipped a grammatical
 * error: "в Андижанская область" instead of "в Андижанской области". These are
 * the head-term landing pages ("тендеры в Ташкенте"), so it is worth getting
 * right, and Russian declension can't be derived reliably from the nominative —
 * "Республика Каракалпакстан" and "город Ташкент" both decline differently from
 * the twelve "…ская область" names.
 *
 * Each country's administrative divisions are a fixed set, so an explicit table
 * per country is simpler and safer than any runtime declension. Keyed by the
 * region CODE (the `/region/:slug` param), not the name, so it is independent of
 * which language the API renders names in.
 *
 * ⚠️ The tables are PER COUNTRY because the codes collide: `KAR` is the Republic
 * of Karakalpakstan in Uzbekistan and Karaganda oblast in Kazakhstan. With one
 * Uzbek table for both, kaztender.com/region/KAR read «Тендеры в Республике
 * Каракалпакстан» in its title, H1, description and JSON-LD (audit 2026-10-16).
 *
 * Uzbek and Kazakh need no equivalent: their strings put the name before the
 * noun ("{{region}}: tenderlar…", "{{region}} тендерлері").
 */
const UZ_RU_LOCATIVE: Record<string, string> = {
  AND: 'Андижанской области',
  BUK: 'Бухарской области',
  FER: 'Ферганской области',
  JIZ: 'Джизакской области',
  KAR: 'Республике Каракалпакстан',
  KAS: 'Кашкадарьинской области',
  KHO: 'Хорезмской области',
  NAM: 'Наманганской области',
  NAV: 'Навоийской области',
  SAM: 'Самаркандской области',
  SIR: 'Сырдарьинской области',
  SUR: 'Сурхандарьинской области',
  TAS: 'Ташкентской области',
  // "в Ташкенте" rather than "в городе Ташкенте" — it is what people search.
  TSH: 'Ташкенте',
};

/**
 * Kazakhstan: the 20 regions of `kato-region.map.ts` (17 oblasts + 3 cities of
 * republican significance), same codes as the `regions` table the API serves.
 * The cities keep the «г.» the API names them with; «Алматы» does not decline.
 */
const KZ_RU_LOCATIVE: Record<string, string> = {
  AKM: 'Акмолинской области',
  AKT: 'Актюбинской области',
  ALM: 'Алматинской области',
  ATY: 'Атырауской области',
  ZKO: 'Западно-Казахстанской области',
  JAM: 'Жамбылской области',
  KAR: 'Карагандинской области',
  KOS: 'Костанайской области',
  KZY: 'Кызылординской области',
  MAN: 'Мангистауской области',
  PAV: 'Павлодарской области',
  SKO: 'Северо-Казахстанской области',
  TUR: 'Туркестанской области',
  VKO: 'Восточно-Казахстанской области',
  ABA: 'Абайской области',
  JET: 'Жетысуской области',
  ULY: 'Улытауской области',
  AST: 'г. Астане',
  ALA: 'г. Алматы',
  SHY: 'г. Шымкенте',
};

/**
 * Kyrgyzstan: the 7 oblasts + 2 cities of republican significance, by the codes
 * `services/api/src/modules/ingestion/kg-region.map.ts` seeds (`kg-seed.ts`).
 */
const KG_RU_LOCATIVE: Record<string, string> = {
  BSK: 'г. Бишкеке',
  OSC: 'г. Оше',
  CHU: 'Чуйской области',
  OSH: 'Ошской области',
  JAL: 'Жалал-Абадской области',
  ISK: 'Иссык-Кульской области',
  NAR: 'Нарынской области',
  TAL: 'Таласской области',
  BAT: 'Баткенской области',
};

const RU_LOCATIVE: Record<Country, Record<string, string>> = {
  uz: UZ_RU_LOCATIVE,
  kz: KZ_RU_LOCATIVE,
  kg: KG_RU_LOCATIVE,
};

/**
 * The RU name to interpolate after the preposition «в», from the table of the
 * country this build serves (`country` is a parameter only so tests can pin
 * both).
 *
 * Falls back to `fallback` (the API's nominative name) for an unknown code, which
 * is the pre-existing behaviour — a new region would read slightly wrong rather
 * than blank. `region-locative.test.ts` asserts every live code is covered.
 */
export function ruRegionLocative(
  code: string | undefined,
  fallback: string,
  country: Country = countryConfig.code,
): string {
  if (!code) return fallback;
  return RU_LOCATIVE[country]?.[code.toUpperCase()] ?? fallback;
}

/** Exported for the coverage test. */
export const RU_LOCATIVE_CODES = Object.keys(UZ_RU_LOCATIVE);
/** Exported for the coverage test. */
export const KZ_RU_LOCATIVE_CODES = Object.keys(KZ_RU_LOCATIVE);
/** Exported for the coverage test. */
export const KG_RU_LOCATIVE_CODES = Object.keys(KG_RU_LOCATIVE);
